/**
 * Aging Table Component
 * Displays aging analysis of fee payments with due dates and delay status
 * Uses fetchTransactions API data (collections) for all calculations
 */
import React, { Component } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import LoadingComponent from '../../loadingComponent';

export class AgingTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            agingMainData: [],
            statusTableData: [],
            paymentModeTableData: [],
            noOfChequeBounces: 0,
            isLoading: false
        };

        // Bind template methods for main aging table
        this.dueDateBodyTemplate = this.dueDateBodyTemplate.bind(this);
        this.installmentNameBodyTemplate = this.installmentNameBodyTemplate.bind(this);
        this.installmentAmountBodyTemplate = this.installmentAmountBodyTemplate.bind(this);
        this.paidAmountBodyTemplate = this.paidAmountBodyTemplate.bind(this);
        this.paidDateBodyTemplate = this.paidDateBodyTemplate.bind(this);
        this.paymentModeBodyTemplate = this.paymentModeBodyTemplate.bind(this);
        this.statusBodyTemplate = this.statusBodyTemplate.bind(this);

        // Bind methods
        this.processTransactionsData = this.processTransactionsData.bind(this);
    }

    async componentDidMount() {
        console.log('AgingTable component mounted');
        
        // Check if transactionsData is already passed as prop
        const { transactionsData, transactionsLoading } = this.props;

        if (transactionsData) {
            // Use data from props
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false) {
            // Data already loaded but empty
            this.setState({ isLoading: false });
        }
    }

    processTransactionsData(data) {
        console.log('Aging - Processing transactions data:', data);

        // Access the collections property
        const transactions = data?.collections;
        const noOfChequeBounces = data?.noOfChequeBounces || 0;
        console.log('Aging - Transactions from collections:', transactions);
        console.log('Aging - Number of Cheque Bounces:', noOfChequeBounces);

        if (transactions && Array.isArray(transactions) && transactions.length > 0) {
            // Transform transactions into aging data
            const agingData = this.transformTransactionsToAgingData(transactions);
            const statusData = this.calculateStatusTable(agingData);
            const paymentModeData = this.calculatePaymentModeTable(transactions);

            this.setState({
                agingMainData: agingData,
                statusTableData: statusData,
                paymentModeTableData: paymentModeData,
                noOfChequeBounces: noOfChequeBounces,
                isLoading: false
            });

            console.log('Aging data loaded successfully:', {
                agingMainData: agingData.length,
                statusTableData: statusData.length,
                paymentModeTableData: paymentModeData.length,
                noOfChequeBounces: noOfChequeBounces
            });
        } else {
            console.warn('No valid transactions in collections');
            this.setState({
                agingMainData: [],
                statusTableData: [],
                paymentModeTableData: [],
                noOfChequeBounces: 0,
                isLoading: false
            });
        }
    }

    componentDidUpdate(prevProps) {
        // Check if transactionsData prop has changed
        const { transactionsData, transactionsLoading } = this.props;
        
        if (transactionsData !== prevProps.transactionsData && transactionsData) {
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false && prevProps.transactionsLoading === true && !transactionsData) {
            // Loading finished but no data
            this.setState({ isLoading: false });
        }
    }

    transformTransactionsToAgingData(transactions) {
        const agingMap = new Map();

        transactions.forEach(transaction => {
            if (!transaction.feePaidDetails || !Array.isArray(transaction.feePaidDetails)) {
                return;
            }

            transaction.feePaidDetails.forEach(feeDetail => {
                const installmentName = feeDetail.paymentTerm || 'Advance';
                const feeType = feeDetail.feeType || '';
                const isAdvance = feeType === 'Advance';
                
                // If feeType is Advance, set dueDate to null, otherwise use the actual dueDate
                const dueDate = isAdvance ? null : (feeDetail.dueDate || transaction.transactionDate);
                const paidDate = transaction.receiptDate  || transaction.transactionDate;
                const paymentMode = transaction.paymentMode || 'N/A';
                const amountPaying = feeDetail.amountPaying || 0;

                // Calculate delay status only if not Advance
                const delayInfo = isAdvance ? { status: null, delayDays: 0 } : this.calculateDelayStatus(dueDate, paidDate);

                const key = `${installmentName}_${dueDate}_${transaction.receiptNo || transaction.transactionDate}`;

                if (agingMap.has(key)) {
                    const existing = agingMap.get(key);
                    existing.paidAmount += amountPaying;
                    // Keep the earliest paid date
                    if (new Date(paidDate) < new Date(existing.paidDate)) {
                        existing.paidDate = paidDate;
                        existing.paymentMode = paymentMode;
                        existing.status = delayInfo.status;
                        existing.delayDays = delayInfo.delayDays;
                    }
                } else {
                    agingMap.set(key, {
                        dueDate: dueDate,
                        installmentName: installmentName,
                        installmentAmount: amountPaying,
                        paidAmount: amountPaying,
                        paidDate: paidDate,
                        paymentMode: paymentMode,
                        status: delayInfo.status,
                        delayDays: delayInfo.delayDays,
                        feeType: feeType
                    });
                }
            });
        });

        return Array.from(agingMap.values()).sort((a, b) =>
            new Date(a.dueDate) - new Date(b.dueDate)
        );
    }

    calculateStatusTable(agingData) {
        const statusCounts = {
            advanced: 0,
            ontime: 0,
            delayed: 0
        };

        agingData.forEach(item => {
            if (item.status === 'advanced') statusCounts.advanced++;
            else if (item.status === 'ontime') statusCounts.ontime++;
            else if (item.status === 'delayed') statusCounts.delayed++;
        });

        return [
            { status: 'Advanced', count: statusCounts.advanced },
            { status: 'Ontime', count: statusCounts.ontime },
            { status: 'Delayed', count: statusCounts.delayed }
        ];
    }

    calculatePaymentModeTable(transactions) {
        const paymentModeMap = new Map();

        transactions.forEach(transaction => {
            const mode = transaction.paymentMode || 'N/A';
            const count = paymentModeMap.get(mode) || 0;
            paymentModeMap.set(mode, count + 1);
        });

        return Array.from(paymentModeMap.entries()).map(([mode, count]) => ({
            paymentMode: mode,
            noOfTimes: count
        }));
    }

    formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    calculateDelayStatus = (dueDate, paidDate) => {
        if (!paidDate || !dueDate) {
            return { status: 'pending', delayDays: 0 };
        }

        const due = new Date(dueDate);
        const paid = new Date(paidDate);

        // Set both dates to midnight for accurate day comparison
        due.setHours(0, 0, 0, 0);
        paid.setHours(0, 0, 0, 0);

        const diffTime = paid - due;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return { status: 'advanced', delayDays: Math.abs(diffDays) };
        } else if (diffDays === 0) {
            return { status: 'ontime', delayDays: 0 };
        } else {
            return { status: 'delayed', delayDays: diffDays };
        }
    };

    // Body Template Methods for Main Aging DataTable
    dueDateBodyTemplate(rowData) {
        if (!rowData.dueDate) return <span>—</span>;
        return <span>{this.formatDate(rowData.dueDate)}</span>;
    }

    installmentNameBodyTemplate(rowData) {
        return <span>{rowData.installmentName}</span>;
    }

    installmentAmountBodyTemplate(rowData) {
        return <span>{rowData.installmentAmount?.toFixed(2)}</span>;
    }

    paidAmountBodyTemplate(rowData) {
        return <span>{rowData.paidAmount?.toFixed(2)}</span>;
    }

    paidDateBodyTemplate(rowData) {
        return <span>{this.formatDate(rowData.paidDate)}</span>;
    }

    paymentModeBodyTemplate(rowData) {
        return <span>{rowData.paymentMode}</span>;
    }

    statusBodyTemplate(rowData) {
        const { status, delayDays } = rowData;

        // If status is null or undefined (for Advance fees), show blank
        if (!status) {
            return <span>—</span>;
        }

        if (status === 'delayed') {
            return (
                <span style={{ color: '#E74C3C', fontWeight: 500 }}>
                    {delayDays} Days Delayed
                </span>
            );
        } else if (status === 'ontime') {
            return (
                <span style={{ color: '#076EFF', fontWeight: 500 }}>
                    On time
                </span>
            );
        } else if (status === 'advanced') {
            return (
                <span style={{ color: '#27AE60', fontWeight: 500 }}>
                    {delayDays} Days Advanced
                </span>
            );
        } else {
            return <span>—</span>;
        }
    }

    // Body Templates for Status Table
    statusNameBodyTemplate(rowData) {
        return <span>{rowData.status}</span>;
    }

    statusCountBodyTemplate(rowData) {
        const isDelayed = rowData.status === 'Delayed';
        return (
            <span style={{
                color: isDelayed ? '#E74C3C' : '#374151',
                fontWeight: isDelayed ? 600 : 500
            }}>
                {rowData.count}
            </span>
        );
    }

    // Body Templates for Payment Mode Table
    paymentModeNameBodyTemplate(rowData) {
        return <span>{rowData.paymentMode}</span>;
    }

    paymentModeCountBodyTemplate(rowData) {
        return <span>{rowData.noOfTimes}</span>;
    }

    render() {
        const { agingMainData, statusTableData, paymentModeTableData, noOfChequeBounces, isLoading } = this.state;

        return (
            <div className="aging-table-container">
                {isLoading && <LoadingComponent />}

                {!isLoading && agingMainData.length === 0 ? (
                    <div className="empty-state-container" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '3rem',
                        minHeight: '400px',
                        color: '#6c757d'
                    }}>
                        <i className="pi pi-inbox" style={{ fontSize: '3rem', marginBottom: '1rem', color: '#dee2e6' }}></i>
                        <h3 style={{ margin: '0 0 0.5rem 0', color: '#495057' }}>No Aging Data Found</h3>
                        <p style={{ margin: 0, fontSize: '0.95rem' }}>There are no aging records available for this student.</p>
                    </div>
                ) : null}

                {!isLoading && agingMainData.length > 0 && (
                    <>
                        {/* Main Aging Table */}
                        <div className="aging-table-wrapper">
                            <DataTable
                                value={agingMainData}
                                className="aging-main-table"
                                showGridlines
                            >
                                <Column
                                    field="dueDate"
                                    header="Due Date"
                                    body={this.dueDateBodyTemplate}
                                    style={{ minWidth: '120px' }}
                                />
                                <Column
                                    field="installmentName"
                                    header="Installment Name"
                                    body={this.installmentNameBodyTemplate}
                                    style={{ minWidth: '180px' }}
                                />
                                <Column
                                    field="installmentAmount"
                                    header="Installment Amount"
                                    body={this.installmentAmountBodyTemplate}
                                    style={{ minWidth: '160px', textAlign: 'right' }}
                                />
                                <Column
                                    field="paidAmount"
                                    header="Paid Amount"
                                    body={this.paidAmountBodyTemplate}
                                    style={{ minWidth: '140px', textAlign: 'right' }}
                                />
                                <Column
                                    field="paidDate"
                                    header="Paid Date"
                                    body={this.paidDateBodyTemplate}
                                    style={{ minWidth: '120px' }}
                                />
                                <Column
                                    field="paymentMode"
                                    header="Payment Mode"
                                    body={this.paymentModeBodyTemplate}
                                    style={{ minWidth: '140px' }}
                                />
                                <Column
                                    field="status"
                                    header="Status"
                                    body={this.statusBodyTemplate}
                                    style={{ minWidth: '160px' }}
                                />
                            </DataTable>
                        </div>

                        {/* Summary Tables Section - All three in one row */}
                        <div className="grid" style={{ marginTop: '1.5rem' }}>
                            {/* Status Table - 4 cols */}
                            <div className="col-12 md:col-4">
                                <h3 className="summary-section-title">Status</h3>
                                <div className="aging-summary-wrapper">
                                    <DataTable
                                        value={statusTableData.length > 0 ? statusTableData : []}
                                        className="aging-summary-table"
                                        showGridlines
                                        stripedRows
                                        emptyMessage="No status data available"
                                    >
                                        <Column
                                            field="status"
                                            header="Status"
                                            body={this.statusNameBodyTemplate}
                                        />
                                        <Column
                                            field="count"
                                            header="Count"
                                            body={this.statusCountBodyTemplate}
                                            style={{ textAlign: 'center' }}
                                        />
                                    </DataTable>
                                </div>
                            </div>

                            {/* Payment Mode Table - 4 cols */}
                            <div className="col-12 md:col-4">
                                <h3 className="summary-section-title">Payment Mode</h3>
                                <div className="aging-summary-wrapper">
                                    <DataTable
                                        value={paymentModeTableData.length > 0 ? paymentModeTableData : []}
                                        className="aging-summary-table"
                                        showGridlines
                                        stripedRows
                                        emptyMessage="No payment mode data available"
                                    >
                                        <Column
                                            field="paymentMode"
                                            header="Payment Mode"
                                            body={this.paymentModeNameBodyTemplate}
                                        />
                                        <Column
                                            field="noOfTimes"
                                            header="No.of Times"
                                            body={this.paymentModeCountBodyTemplate}
                                            style={{ textAlign: 'center' }}
                                        />
                                    </DataTable>
                                </div>
                            </div>

                            {/* Cheque Bounce Count Display - 4 cols */}
                            <div className="col-12 md:col-4">
                                <h3 className="summary-section-title">No.of Cheque Bounce</h3>
                                <div className="aging-summary-wrapper">
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        padding: '2rem',
                                        minHeight: '120px'
                                    }}>
                                        <span style={{ 
                                            fontSize: '3rem', 
                                            fontFamily: 'InterBold',
                                            color: noOfChequeBounces > 0 ? '#DC2626' : '#6B7280',
                                            fontWeight: 700
                                        }}>
                                            {noOfChequeBounces}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    }
}
